import { useMutation, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import {
  deleteMemory,
  deleteUserMemories,
  patchMemory,
  postMemory,
} from "../methods/memoryMethods";
import { MEMORIES_QUERY_KEY } from "utilities/memory";

/**
 * Every memory write invalidates all memory lists, because a memory about the
 * user appears in every chat's list.
 */
function useInvalidateMemories() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MEMORIES_QUERY_KEY });
}

export function usePostMemoryMutation() {
  const getAccessToken = useGetAccessToken();
  const invalidate = useInvalidateMemories();

  return useMutation({
    mutationFn: (input: { content: string; threadId?: string }) =>
      postMemory(input, getAccessToken),
    onSuccess: invalidate,
  });
}

export function usePatchMemoryMutation() {
  const getAccessToken = useGetAccessToken();
  const invalidate = useInvalidateMemories();

  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      patchMemory(id, content, getAccessToken),
    onSuccess: invalidate,
  });
}

export function useDeleteMemoryMutation() {
  const getAccessToken = useGetAccessToken();
  const invalidate = useInvalidateMemories();

  return useMutation({
    mutationFn: (id: string) => deleteMemory(id, getAccessToken),
    onSuccess: invalidate,
    onError: (error) => console.error("Memory delete failed:", error),
  });
}

export function useDeleteUserMemoriesMutation() {
  const getAccessToken = useGetAccessToken();
  const invalidate = useInvalidateMemories();

  return useMutation({
    mutationFn: () => deleteUserMemories(getAccessToken),
    onSuccess: invalidate,
    onError: (error) => console.error("Memory delete-all failed:", error),
  });
}
